/*
 * (c) Meta Platforms, Inc. and its affiliates.
 */

import React from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { SearchBoxOptions, DataPoint } from "../../utils/DemoTypes";
import ReactFilterBox, {
	SimpleResultProcessing,
	Expression,
} from "react-filter-box";
import { useDebounce, useCopyToClipboard } from "react-use";
import { FaClipboard } from "react-icons/fa";
//import useRemoveWheelInteraction from "../../hooks/useRemoveWheelInteractions";

type SearchBoxCardProps = {
	unfilteredData: any[];
	options: SearchBoxOptions[];
	setFilteredData: (entry: DataPoint[]) => void;
};

const SearchBoxCard = ({
	unfilteredData,
	options,
	setFilteredData,
}: SearchBoxCardProps) => {
	const [currentQueryParameters, setSearchParams] = useSearchParams();
	const view_query = currentQueryParameters.get("query") as string;
	const view_expressions = currentQueryParameters.get("expressions") as string;
	const location = useLocation();
	const searchParams: URLSearchParams = new URLSearchParams(location.search);

	const [currentExpresions, setCurrentExpressions] = React.useState<
		Expression[]
	>([]);
	const [query, setQuery] = React.useState<string>(view_query);
	const [, copyToClipboard] = useCopyToClipboard();

	/**
	 * When component mounts, check for expressions passed via
	 * URL params.
	 */
	React.useEffect(() => {
		if (view_expressions) {
			onParseOk(JSON.parse(view_expressions));
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Parse query formatted as expressions to
	 * filter data.
	 */
	const onParseOk = React.useCallback(
		(expressions: Expression[]) => {
			let newData = new SimpleResultProcessing(options).process(
				unfilteredData,
				expressions
			);
			setFilteredData(newData);
			setCurrentExpressions(expressions);
		},
		[options, unfilteredData, setFilteredData]
	);

	/**
	 * Debounce changes on query and expressions to encode in URL.
	 */
	useDebounce(
		() => {
			searchParams.set("query", query);
			searchParams.set("expressions", JSON.stringify(currentExpresions));
			setSearchParams(searchParams, { replace: true });
		},
		250,
		[query, currentExpresions]
	);

	/**
	 * Generate url based on origin + path + search.
	 * @returns
	 */
	const getLinkToShare = () => {
		let shareLink = window.location.href;
		return shareLink;
	};

	return (
		<div
			className="absolute top-20 left-5 w-[192px] md:w-[50vw] max-w-[400px] z-[9] bg-secondary rounded-md p-2"
			style={{ pointerEvents: "none" }}
		>
			<div style={{ pointerEvents: "auto" }}>
				<div className="flex justify-between content-center mb-1">
					<p className="text-sm text-white mb-2 font-bold">Select Data</p>
					<div
						className="tooltip tooltip-right tooltip-warning"
						data-tip="Copy and share this view."
					>
						<button
							className="btn btn-xs text-xs normal-case"
							onClick={() => copyToClipboard(getLinkToShare())}
						>
							<FaClipboard />
						</button>
					</div>
				</div>
				<ReactFilterBox
					data={unfilteredData}
					options={options}
					onParseOk={onParseOk}
					query={query}
					strictMode={false}
					onChange={(e: string) => setQuery(e)}
				/>
			</div>
		</div>
	);
};

export default SearchBoxCard;
